import { useEffect, useState } from "react";
import { ArtCrop, CategoryLine, cardAlt, RESPONSE_KEYS } from "@/components/game/CardArt";
import { CompactStats } from "@/components/game/CardStats";
import { printedName } from "@/lib/artAssets";
import Coin from "@/components/game/Coin";
import { PLAYER_COLORS } from "@/lib/api";

// Card face drawn at 112x202 and scaled to `w` (cards shrink to fit, proportions kept).
export const FACE_W = 112;
export const FACE_H = 202;
export const faceH = (w) => Math.round((w * FACE_H) / FACE_W);

// paid: PAY UPFRONT — a small "Paid" mark on hand cards (they were paid when taken).
export function CardFace({ card, w = FACE_W, testid, nameTestid, statsTestid, statTestid, selected = false, outline = "var(--sea)", dimmed = false, note, cost = null, costTestid, paid = false, paidTestid, onClick, onHover }) {
  const scale = w / FACE_W;
  return (
    <div className="shrink-0 relative" style={{ width: w, height: faceH(w) }}>
      <button
        type="button"
        data-testid={testid}
        data-card-key={card.key}
        aria-pressed={selected}
        aria-label={cardAlt(card)}
        title={note}
        onClick={onClick}
        onMouseEnter={() => onHover?.(card)}
        onMouseLeave={() => onHover?.(null)}
        onFocus={() => onHover?.(card)}
        onBlur={() => onHover?.(null)}
        className="hand-lift absolute left-0 top-0 flex flex-col text-left"
        style={{
          width: FACE_W,
          height: FACE_H,
          transform: scale === 1 ? undefined : `scale(${scale})`,
          transformOrigin: "left top",
          padding: 6,
          borderRadius: 9,
          background: "var(--card)",
          border: "1px solid var(--card-border)",
          boxShadow: selected ? `0 0 0 ${Math.max(2, 2.5 / scale)}px ${outline}, 0 6px 16px rgba(36,40,38,.12)` : "none",
          opacity: dimmed ? 0.6 : 1,
        }}
      >
        <ArtCrop card={card} size={FACE_W - 12} radius={5} />
        <span className="flex-1 min-h-0 flex flex-col" style={{ padding: "7px 3px 1px" }}>
          <CategoryLine card={card} fontSize={8.5} />
          <span className="mt-0.5 line-clamp-2" data-testid={nameTestid} style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2, color: "var(--ink)" }}>{printedName(card)}</span>
          <span className="flex-1" />
          <span className="overflow-hidden" data-testid={statsTestid} style={{ fontSize: 10, lineHeight: "12px", maxHeight: 30, color: "var(--ink-4)", borderTop: "1px solid var(--line-soft)", paddingTop: 4, overflowWrap: "normal", wordBreak: "normal" }}>
            <CompactStats card={card} testid={statTestid} />
          </span>
        </span>
        {paid && (
          <span data-testid={paidTestid} className="absolute inline-flex items-center" style={{ top: 10, right: 10, padding: "1px 5px", borderRadius: 7, background: "rgba(251,250,247,0.94)", fontSize: 9.5, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--sea)", boxShadow: "0 1px 3px rgba(0,0,0,.15)" }}>
            Paid
          </span>
        )}
        {cost !== null && (
          <span data-testid={costTestid} className="absolute inline-flex items-center gap-1" style={{ top: 10, right: 10, padding: "2px 6px", borderRadius: 8, background: "var(--card)", fontSize: 13, fontWeight: 800, color: "var(--sea)", boxShadow: "0 1px 3px rgba(0,0,0,.15)" }}>
            <Coin size={13} /> {cost}
          </span>
        )}
      </button>
    </div>
  );
}

// YOUR HAND (right column, under the marketplace), tinted in the player's colour:
// build cards in the first row, held action cards (up to actionMax) in the second.
// Only this panel may scroll (inside itself) if a house-rule hand is very big.
// Both rows always show in full: the cards shrink (proportions kept) to the panel's height.
const ROW_GAP = 8;
const MIN_CARD_W = 64;

// The card width that fits two rows (build + action) into the panel's measured height.
function useFitWidth(cardW) {
  const [el, setEl] = useState(null); // callback ref: the panel may mount later (no player yet)
  const [h, setH] = useState(0);
  useEffect(() => {
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([e]) => setH(Math.floor(e.contentRect.height)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  const fit = h > 0 ? Math.floor((((h - ROW_GAP) / 2) * FACE_W) / FACE_H) : cardW;
  return [setEl, Math.max(MIN_CARD_W, Math.min(cardW, fit))];
}

export default function HandRow({ player, seatNo, cardW: maxCardW = 104, actionMax = 3, selectedCardIdx, selectedActionId = null, activeCardId, ownTurnAction, paid = false, onCardClick, onActionClick, onCardHover, regionStyle, actionsFaceUp = true }) {
  const [scrollRef, cardW] = useFitWidth(maxCardW);
  if (!player) return null;
  const color = PLAYER_COLORS[player.color];
  const hand = player.hand || [];
  const actions = player.action_cards || [];
  const actionCount = actionsFaceUp ? actions.length : player.action_card_count ?? actions.length;
  const gap = ROW_GAP;
  const rowW = 4 * cardW + 3 * gap;
  const w = hand.length > 4 ? Math.floor((rowW - (hand.length - 1) * gap) / hand.length) : cardW;
  const ph = { width: cardW, height: faceH(cardW), borderRadius: 9 * (cardW / FACE_W), border: "1.5px dashed color-mix(in srgb, " + color + " 45%, #C9C3B6)" };
  return (
    <section
      data-testid="player-hand"
      data-player={player.color}
      className="focus-region flex-1 min-h-0 flex flex-col"
      style={{ boxSizing: "border-box", background: `color-mix(in srgb, ${color} 12%, var(--card))`, border: `1px solid color-mix(in srgb, ${color} 40%, transparent)`, borderRadius: 14, padding: "8px 12px 10px", ...regionStyle }}
    >
      <div className="flex items-center gap-2 shrink-0 whitespace-nowrap" style={{ marginBottom: 7, height: 16, lineHeight: "16px" }}>
        <span aria-hidden="true" data-testid="hand-color-chip" className="shrink-0" style={{ width: 10, height: 10, borderRadius: 3, background: color }} />
        <span className="section-label" data-testid="hand-label">Your hand</span>
        <span data-testid="hand-summary" className="truncate" style={{ fontSize: 12, color: "var(--ink-3)" }}>
          {player.name}{seatNo ? ` (P${seatNo})` : ""} · {hand.length} build card{hand.length === 1 ? "" : "s"} · {actionCount} action card{actionCount === 1 ? "" : "s"}
        </span>
      </div>
      <div ref={scrollRef} data-testid="hand-scroll" data-card-w={cardW} className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col" style={{ gap }}>
        <div data-testid="player-hand-grid" className="flex flex-nowrap shrink-0" style={{ gap }}>
          {hand.map((card, i) => {
            const response = ownTurnAction && RESPONSE_KEYS.has(card.key);
            return (
              <CardFace
                key={card.id}
                card={card}
                w={w}
                testid={`hand-card-${i}-${card.kind}`}
                nameTestid={`hand-card-name-${i}`}
                statsTestid={`hand-card-stats-${i}`}
                statTestid={`hand-card-stat-${i}`}
                selected={i === selectedCardIdx || card.id === activeCardId}
                outline={color}
                dimmed={response}
                paid={paid}
                paidTestid={`hand-card-paid-${i}`}
                note={response ? "Response card: played when it's triggered" : paid ? "Paid when taken: free to play" : undefined}
                onClick={() => onCardClick(card, i)}
                onHover={onCardHover}
              />
            );
          })}
        </div>
        <div data-testid="player-hand-actions" data-count={actionCount} className="flex flex-nowrap shrink-0" style={{ gap }}>
          {Array.from({ length: Math.max(actionMax, actionCount) }, (_, i) => {
            const card = actionsFaceUp ? actions[i] : null;
            if (!card) return <span key={`ph-${i}`} aria-hidden="true" data-testid={`held-action-slot-${i}`} className="shrink-0" style={ph} />;
            return (
              <CardFace
                key={card.id}
                card={card}
                w={cardW}
                testid={`hand-action-${i}`}
                nameTestid={`held-action-name-${i}`}
                selected={card.id === selectedActionId || card.id === activeCardId}
                outline={color}
                dimmed={ownTurnAction && RESPONSE_KEYS.has(card.key)}
                paid
                paidTestid={`held-action-paid-${i}`}
                note={RESPONSE_KEYS.has(card.key) ? "Response card: played when it's triggered" : card.name}
                onClick={() => onActionClick?.(card, i)}
                onHover={onCardHover}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}
